import _ from 'lodash';
import update from 'immutability-helper';
import axios from 'axios';
import qwPartners from '../common/clients/qwPartners';
import I18n from '../../config/i18n';
import * as enums from '../common/utils/enums';
import { updateWorkOrderWithMovieData } from '../common/utils/helpers';
import { handleAppError, showAppBusy } from './app';

const initialState = {
  workOrders: [],
  dataReceived: false,
  dcpTransferStatuses: [
    { label: 'DCP Transfer started', value: 'started' },
    { label: 'DCP Transfer completed', value: 'completed' },
  ],
  itemsFilter: enums.WorkOrderItemsFilterValues.CURRENT,
  offset: 0,
  pageSize: 25,
  page: 1,
  workOrdersCount: {
    archivedWorkOrdersCount: 0,
    activeWorkOrdersCount: 0,
  },
  hasMoreData: false,
  loading: false,
  movieDataLoading: false,
  WorkOrderEnabledExpandAll: false,
  workOrderIds: [],
};

const RECEIVE = 'workOrders/RECEIVE';
const ERROR = 'workOrders/ERROR';
const RECEIVE_WORK_ORDERS_COUNT = 'workOrders/RECEIVE_WORK_ORDERS_COUNT';
const RECEIVE_DCP_TRANSFER_STATUSES =
  'workOrders/RECEIVE_DCP_TRANSFER_STATUSES';
const UPDATE_DCP_TRANSFER_STATUS = 'workOrders/UPDATE_DCP_TRANSFER_STATUS';
const UPDATE_ITEMS_FILTER = 'workOrders/UPDATE_ITEMS_FILTER';
const UPDATE_GET_ELAPSED_WORK_ORDERS =
  'workOrders/UPDATE_GET_ELAPSED_WORK_ORDERS';
const OVERALL_WORK_ORDER_STATUS_UPDATED_IN_SERVER =
  'workOrders/OVERALL_WORK_ORDER_STATUS_UPDATED_IN_SERVER';
const RESET = 'workOrders/RESET';
const LOADING_STATUS = 'workOrders/LOADING_STATUS';
const MOVIE_DATA_LOADING_STATUS = 'workOrders/MOVIE_DATA_LOADING_STATUS'; // New action type
const WO_LIST_EXPAND_TOGGLE = 'workOrders/WO_LIST_EXPAND_TOGGLE';
const SINGLE_TOGGLE_EXPAND = 'workOrders/SINGLE_TOGGLE_EXPAND';

const CancelToken = axios.CancelToken;
let source = CancelToken.source();

export function loadWorkOrders(
  itemsFilter = enums.WorkOrderItemsFilterValues.CURRENT,
  pageSize = initialState.pageSize,
  page = initialState.page,
  search = '',
) {
  return (dispatch, getState) => {
    // Using cancel token because when we jump between active and archive pages,
    // the previous request is satisfied and being rendered instead of current one.
    if (source) {
      source.cancel();
      source = CancelToken.source();
    }
    dispatch(setLoadingStatus(true));
    dispatch(resetWorkOrders());
    const { user } = getState().account;
    const applianceCount = user.appliances.length;
    const currentOffset =
      page === 1 ? 0 : initialState.offset + pageSize * (page - 1);
    const searchParam = search ? `&search=${search}` : '';

    return qwPartners
      .get(
        `/v1/work-orders?items=${itemsFilter}&appliancesCount=${applianceCount}&offset=${currentOffset}&ps=${pageSize}${searchParam}`,
        { cancelToken: source.token },
      )
      .then((res) => res.data)
      .then((WorkOrders) => {
        const workOrders = WorkOrders;
        // Changing mode since we use pagination
        const operation = '$set';
        const hasMoreData = pageSize === WorkOrders.length;
        dispatch(setLoadingStatus(false));
        dispatch(
          receiveWorkOrders(
            workOrders,
            currentOffset,
            operation,
            hasMoreData,
            pageSize,
            page,
          ),
        );
        if (workOrders.length > 0) {
          // The movie data loading state will now be handled in fetchMappedMovie
          dispatch(
            fetchMappedMovie(
              workOrders,
              currentOffset,
              operation,
              hasMoreData,
              pageSize,
              page,
            ),
          );
        }
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          dispatch(updatePageError());
          dispatch(handleAppError(error, I18n.t('couldNotLoadWorkOrders')));
        }
      });
  };
}

export function loadWorkOrdersCount() {
  return (dispatch) => {
    return qwPartners
      .get('/v1/work-orders-count')
      .then((res) => res.data)
      .then((workOrdersCount) => {
        dispatch(receiveWorkOrdersCount(workOrdersCount));
      })
      .catch((error) => {
        dispatch(updatePageError());
        dispatch(handleAppError(error, I18n.t('couldNotLoadWorkOrders')));
      });
  };
}

export function fetchMappedMovie(
  workOrders,
  currentOffset,
  operation,
  hasMoreData,
  pageSize,
  page,
) {
  return (dispatch) => {
    // Use the new movie-specific loading state instead of the general one
    dispatch(setMovieDataLoadingStatus(true));

    const workOrdersCompositions = workOrders.map((compositionsList) =>
      compositionsList.compositions.map(
        (composition) => composition.composition,
      ),
    );
    const result = workOrdersCompositions.flat();
    const uniqueCompositions = result.filter(
      (composition, index) => result.indexOf(composition) === index,
    );

    qwPartners
      .post(`/v1/movies`, { compositions: uniqueCompositions })
      .then((response) => {
        const movieData = response.data;
        const updatedWorkOrders = workOrders.map((workOrder) =>
          updateWorkOrderWithMovieData(workOrder, movieData),
        );
        dispatch(
          receiveWorkOrders(
            updatedWorkOrders,
            currentOffset,
            operation,
            hasMoreData,
            pageSize,
            page,
          ),
        );
        dispatch(setMovieDataLoadingStatus(false));
      })
      .catch((error) => {
        dispatch(setMovieDataLoadingStatus(false));
        dispatch(handleAppError(error, I18n.t('couldNotLoadWorkOrders')));
      });
  };
}

export function fetchTaskDetails(taskId) {
  return () => {
    return qwPartners
      .get(`/v1/appliance-tasks/${taskId}`)
      .then((res) => res.data);
  };
}
export function receiveWorkOrders(
  payload,
  offset,
  operation,
  hasMoreData,
  pageSize,
  page,
) {
  return {
    type: RECEIVE,
    payload: payload,
    meta: {
      offset,
      operation,
      hasMoreData,
      pageSize,
      page,
    },
  };
}

export function receiveWorkOrdersCount(workOrdersCount) {
  return { type: RECEIVE_WORK_ORDERS_COUNT, payload: workOrdersCount };
}

export function receiveDcpTransferStatuses(payload) {
  return { type: RECEIVE_DCP_TRANSFER_STATUSES, payload: payload };
}

export function updateDcpTransferStatus(workOrderNo, compositionId, status) {
  return {
    type: UPDATE_DCP_TRANSFER_STATUS,
    payload: status,
    meta: { workOrderNo, compositionId },
  };
}

export function updateItemsFilter(payload) {
  return { type: UPDATE_ITEMS_FILTER, payload };
}

export function updateGetElapsedWorkOrders(payload) {
  return { type: UPDATE_GET_ELAPSED_WORK_ORDERS, payload };
}

export function updatePageError() {
  return { type: ERROR };
}

export function resetWorkOrders() {
  return { type: RESET };
}

export function setLoadingStatus(payload) {
  return { type: LOADING_STATUS, payload };
}

export function setMovieDataLoadingStatus(payload) {
  return { type: MOVIE_DATA_LOADING_STATUS, payload };
}

export const workOrderListToggleExpand = (payload) => {
  return {
    type: WO_LIST_EXPAND_TOGGLE,
    payload,
  };
};

export const workOrderSingleToggleExpand = (payload) => {
  return {
    type: SINGLE_TOGGLE_EXPAND,
    payload,
  };
};

function getworkOrderIdFromCompositions(workOrders) {
  const workOrderId = [];
  workOrders.forEach((workOrder) => {
    workOrderId.push(workOrder.workOrderNo);
  });

  return _.uniq(workOrderId);
}

function getworkOrderIdFromWorkOrder(workOrderNo, getWorkOrders) {
  const workOrderSet = new Set(getWorkOrders);
  if (workOrderSet.has(workOrderNo)) {
    workOrderSet.delete(workOrderNo);
  } else {
    workOrderSet.add(workOrderNo);
  }
  return Array.from(workOrderSet);
}

export function updateDcpTransferStatusInServer(workOrderNo, compositionId) {
  return (dispatch, getState) => {
    const { workOrders } = getState().workOrders;

    const workOrder = _.find(workOrders, { workOrderNo: workOrderNo });

    if (!workOrder) {
      return;
    }

    const composition = _.find(workOrder.compositions, {
      composition: compositionId,
    });

    if (!composition) {
      return;
    }

    const url = `/v1/bookings/compositions/${compositionId}/work-orders/${workOrderNo}/transfer-status`;
    const data = {
      status: composition.dcpTransferStatus,
      bookingNos: _.uniq(_.map(workOrder.shipments, 'bookingNo')),
    };

    dispatch(showAppBusy(true));

    return qwPartners
      .put(url, data)
      .then(() => {
        dispatch(showAppBusy(false));
        dispatch(setOverallWorkOrderStatusUpdatedInServer(workOrderNo, true));
      })
      .catch((error) => {
        dispatch(showAppBusy(false));
        dispatch(
          handleAppError(error, I18n.t('dcpUploadStatusCouldNotUpdated')),
        );
      });
  };
}

export function setOverallWorkOrderStatusUpdatedInServer(workOrderNo, status) {
  return {
    type: OVERALL_WORK_ORDER_STATUS_UPDATED_IN_SERVER,
    payload: status,
    meta: { workOrderNo },
  };
}

export default function reducer(state = initialState, action) {
  const { type, payload, meta } = action;

  switch (type) {
    case RECEIVE:
      return update(state, {
        workOrders: { [meta.operation]: payload },
        offset: { $set: meta.offset },
        hasMoreData: { $set: meta.hasMoreData },
        dataReceived: { $set: true },
        pageSize: { $set: parseInt(meta.pageSize) },
        page: { $set: parseInt(meta.page) },
        hasError: { $set: false },
      });
    case RECEIVE_WORK_ORDERS_COUNT:
      return update(state, {
        workOrdersCount: { $set: payload },
      });

    case RECEIVE_DCP_TRANSFER_STATUSES:
      return update(state, { dcpTransferStatuses: { $set: payload } });

    case UPDATE_DCP_TRANSFER_STATUS: {
      const workOrderIndex = _.findIndex(state.workOrders, {
        workOrderNo: meta.workOrderNo,
      });

      if (workOrderIndex === -1) {
        return state;
      }

      const compositions = state.workOrders[workOrderIndex].compositions;
      const compositionIndex = _.findIndex(compositions, {
        composition: meta.compositionId,
      });

      if (compositionIndex === -1) {
        return state;
      }

      return update(state, {
        workOrders: {
          [workOrderIndex]: {
            compositions: {
              [compositionIndex]: { dcpTransferStatus: { $set: payload } },
            },
          },
        },
      });
    }

    case OVERALL_WORK_ORDER_STATUS_UPDATED_IN_SERVER: {
      const workOrderIndex = _.findIndex(state.workOrders, {
        workOrderNo: meta.workOrderNo,
      });

      if (workOrderIndex === -1) {
        return state;
      }

      return update(state, {
        workOrders: {
          [workOrderIndex]: {
            overallWorkOrderStatusChangedInServer: { $set: payload },
          },
        },
      });
    }

    case UPDATE_ITEMS_FILTER:
      return update(state, {
        itemsFilter: { $set: payload },
      });
    case WO_LIST_EXPAND_TOGGLE: {
      return update(state, {
        WorkOrderEnabledExpandAll: { $set: payload },
        workOrderIds: {
          $set: payload ? getworkOrderIdFromCompositions(state.workOrders) : [],
        },
      });
    }
    case SINGLE_TOGGLE_EXPAND: {
      return update(state, {
        workOrderIds: {
          $set: getworkOrderIdFromWorkOrder(
            payload.workOrderNo,
            payload.workOrderIds,
          ),
        },
      });
    }

    case RESET:
      return update(state, {
        workOrders: { $set: initialState.workOrders },
        dataReceived: { $set: initialState.dataReceived },
        offset: { $set: initialState.offset },
        hasError: { $set: false },
        pageSize: { $set: initialState.pageSize },
        page: { $set: parseInt(initialState.page) },
        getElapsedWorkOrders: { $set: initialState.getElapsedWorkOrders },
      });
    case ERROR:
      return update(state, {
        hasError: { $set: true },
      });
    case LOADING_STATUS:
      return update(state, { loading: { $set: payload } });
    case MOVIE_DATA_LOADING_STATUS:
      return update(state, { movieDataLoading: { $set: payload } });
    default:
      return state;
  }
}
